import { toastError } from '@/lib/toast-error';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { sendDTalkMessage, DTALK_MESSAGE_URL, type DTalkMessageInput, type DTalkMessageResult } from '@/lib/dtalk-api';

// ─── Start / Epic ────────────────────────────────────────────────────────────

export function usePerformanceStartData() {
  return trpc.performance.start.getData.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export function usePerformanceTemplates() {
  return trpc.performance.start.getTemplates.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

// ─── Feedback CRUD ───────────────────────────────────────────────────────────

export function useCreateFeedback() {
  const utils = trpc.useUtils();
  const { t } = useTranslation();
  return trpc.performance.feedback.create.useMutation({
    onSuccess: () => {
      utils.performance.feedback.listByEvaluator.invalidate();
    },
    onError: (err) => {
      toast.error(err.message || t('common.ui.error'));
    },
  });
}

export function useFeedbackById(feedbackId: string) {
  return trpc.performance.feedback.getById.useQuery(
    { id: feedbackId },
    { enabled: !!feedbackId, refetchOnWindowFocus: false },
  );
}

export function useUpdateFeedback() {
  const utils = trpc.useUtils();
  return trpc.performance.feedback.update.useMutation({
    onSuccess: (_, vars) => {
      utils.performance.feedback.getById.invalidate({ id: vars.id });
      utils.performance.feedback.listByEvaluator.invalidate();
    },
  });
}

export function useListFeedbacks(epicType?: 'seasonal' | 'performance' | 'feedback_session') {
  return trpc.performance.feedback.listByEvaluator.useQuery(
    epicType ? { epicType } : {},
    { refetchOnWindowFocus: false, staleTime: 30_000 },
  );
}

export function useReopenFeedback() {
  const utils = trpc.useUtils();
  return trpc.performance.feedback.reopen.useMutation({
    onSuccess: () => {
      utils.performance.feedback.listByEvaluator.invalidate();
    },
  });
}

export function useDeleteFeedback() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.feedback.delete.useMutation({
    onSuccess: () => {
      utils.performance.feedback.listByEvaluator.invalidate();
      toast.success(t('hooks.performance.evaluacion_eliminada'));
    },
  });
}

// ─── Active EPIC ─────────────────────────────────────────────────────────────

export function useActiveEpic() {
  return trpc.performance.epicGlobal.getActive.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function useEpicById(epicId: string) {
  return trpc.performance.epicGlobal.getById.useQuery(
    { id: epicId },
    { enabled: !!epicId, refetchOnWindowFocus: false },
  );
}

// ─── Chat ────────────────────────────────────────────────────────────────────

export function useProcessMessage() {
  return trpc.performance.chat.processMessage.useMutation();
}

// ─── Conversation ────────────────────────────────────────────────────────────

export function useConversationMessages(conversationId: string) {
  return trpc.performance.conversation.getMessages.useQuery(
    { conversationId },
    { enabled: !!conversationId, refetchOnWindowFocus: false },
  );
}

export function useAddConversationMessage() {
  const utils = trpc.useUtils();
  return trpc.performance.conversation.addMessage.useMutation({
    onSuccess: (_, vars) => {
      utils.performance.conversation.getMessages.invalidate({ conversationId: vars.conversationId });
    },
  });
}

// ─── Result Analysis ─────────────────────────────────────────────────────────

export function useGenerateAnalysis() {
  const utils = trpc.useUtils();
  return trpc.performance.resultAnalysis.generate.useMutation({
    onSuccess: (_, vars) => {
      utils.performance.feedback.getById.invalidate({ id: vars.feedbackId });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────────

export function usePerformanceIsAdmin() {
  return trpc.performance.admin.isAdmin.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function usePerformanceStats() {
  return trpc.performance.admin.stats.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

// ─── Reports (direct reports from BQ) ───────────────────────────────────────

export function useDirectReports() {
  return trpc.performance.reports.list.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 120_000,
  });
}

// ─── Impact Levels ───────────────────────────────────────────────────────────

export function useImpactLevels() {
  return trpc.performance.impactLevels.list.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 300_000,
  });
}

// ─── Flavia Config ───────────────────────────────────────────────────────────

export function useAssistantVoice(assistantId: string | undefined) {
  return trpc.performance.flaviaConfig.getVoice.useQuery(
    { assistantId: assistantId ?? 'default' },
    { enabled: !!assistantId, staleTime: 5 * 60_000, refetchOnWindowFocus: false },
  );
}

export function useFlaviaConfigList() {
  return trpc.performance.flaviaConfig.list.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });
}

export function useFlaviaConfig(assistantId: string) {
  return trpc.performance.flaviaConfig.get.useQuery(
    { id: assistantId },
    { enabled: !!assistantId, refetchOnWindowFocus: false },
  );
}

export function useSaveFlaviaConfig() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.flaviaConfig.save.useMutation({
    onSuccess: (_, vars) => {
      utils.performance.flaviaConfig.list.invalidate();
      utils.performance.flaviaConfig.get.invalidate({ id: vars.id });
      toast.success(t('hooks.performance.configuracion_guardada'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_guardar');
    },
  });
}

export function useCreateFlaviaConfig() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.flaviaConfig.create.useMutation({
    onSuccess: () => {
      utils.performance.flaviaConfig.list.invalidate();
      toast.success(t('hooks.performance.asistente_creado'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_crear');
    },
  });
}

export function useDeleteFlaviaConfig() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.flaviaConfig.delete.useMutation({
    onSuccess: () => {
      utils.performance.flaviaConfig.list.invalidate();
      toast.success(t('hooks.performance.asistente_eliminado'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_eliminar');
    },
  });
}

// ─── EPIC Admin ───────────────────────────────────────────────────────────────

export function useEpicList() {
  return trpc.performance.epicGlobal.list.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });
}

export function useCreateEpic() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.epicGlobal.create.useMutation({
    onSuccess: () => {
      utils.performance.epicGlobal.list.invalidate();
      utils.performance.epicGlobal.getActive.invalidate();
      toast.success(t('hooks.performance.epic_creado'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_crear_epic');
    },
  });
}

export function useUpdateEpic() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.epicGlobal.update.useMutation({
    onSuccess: () => {
      utils.performance.epicGlobal.list.invalidate();
      utils.performance.epicGlobal.getActive.invalidate();
      toast.success(t('hooks.performance.epic_actualizado'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_actualizar_epic');
    },
  });
}

export function useCloseEpic() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.epicGlobal.close.useMutation({
    onSuccess: () => {
      utils.performance.epicGlobal.list.invalidate();
      utils.performance.epicGlobal.getActive.invalidate();
      toast.success(t('hooks.performance.epic_cerrado'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_cerrar_epic');
    },
  });
}

// ─── Knowledge Base ───────────────────────────────────────────────────────────

export function useKnowledgeBaseList(epicId: string | null) {
  return trpc.performance.knowledgeBase.list.useQuery(
    { epicId },
    { refetchOnWindowFocus: false },
  );
}

export function useKnowledgeGetUploadUrl() {
  return trpc.performance.knowledgeBase.getUploadUrl.useMutation();
}

export function useKnowledgeProcessFile() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.knowledgeBase.processFile.useMutation({
    onSuccess: () => {
      utils.performance.knowledgeBase.list.invalidate();
      toast.success(t('hooks.performance.documento_procesado'));
    },
  });
}

export function useKnowledgeDelete() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.knowledgeBase.delete.useMutation({
    onSuccess: () => {
      utils.performance.knowledgeBase.list.invalidate();
      toast.success(t('hooks.performance.documento_eliminado'));
    },
  });
}

// ─── Notification Templates ───────────────────────────────────────────────────

export function useDriveSyncConfig() {
  return trpc.performance.knowledgeBase.getDriveSyncConfig.useQuery(undefined, { refetchOnWindowFocus: false });
}

export function useDriveOAuthUrl() {
  return trpc.performance.knowledgeBase.getDriveOAuthUrl.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function useConnectDriveAuth() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.knowledgeBase.connectDriveAuth.useMutation({
    onSuccess: () => {
      utils.performance.knowledgeBase.getDriveSyncConfig.invalidate();
      toast.success(t('hooks.performance.google_drive_conectado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_conectando_drive'),
  });
}

export function useDisconnectDriveSync() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.knowledgeBase.disconnectDriveSync.useMutation({
    onSuccess: () => {
      utils.performance.knowledgeBase.getDriveSyncConfig.invalidate();
      toast.success(t('hooks.performance.drive_desconectado'));
    },
  });
}

export function useTriggerDriveSync() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.knowledgeBase.triggerDriveSync.useMutation({
    onSuccess: () => {
      utils.performance.knowledgeBase.list.invalidate();
      toast.success(t('hooks.performance.sincronizacion_completada'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_sincronizar'),
  });
}

export function useNotificationConfig(notificationType: 'epic_launch' | 'reminder' | 'on_demand') {
  return trpc.performance.notificationTemplates.getConfig.useQuery(
    { notificationType },
    { refetchOnWindowFocus: false },
  );
}

export function useSaveNotificationTemplate() {
  const { t } = useTranslation();
  return trpc.performance.notificationTemplates.save.useMutation({
    onSuccess: () => {
      toast.success(t('hooks.performance.template_guardado'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_guardar_template');
    },
  });
}

export function useSetNotificationEnabled() {
  return trpc.performance.notificationTemplates.setEnabled.useMutation({
    onError: (err) => {
      toastError(err, 'common.toast.error_generico');
    },
  });
}

// ─── User Preferences ────────────────────────────────────────────────────────

export function useUserPreference(key: string) {
  return trpc.performance.userPreferences.get.useQuery(
    { key },
    { enabled: !!key, refetchOnWindowFocus: false },
  );
}

export function useSetUserPreference() {
  return trpc.performance.userPreferences.set.useMutation();
}

// ─── Flow Config ─────────────────────────────────────────────────────────────

export function useFlowConfig() {
  return trpc.performance.flowConfig.get.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function useSetFlowConfig() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.flowConfig.set.useMutation({
    onSuccess: () => {
      utils.performance.flowConfig.get.invalidate();
      toast.success(t('hooks.performance.asistente_actualizado'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_actualizar');
    },
  });
}

// ─── Realtime ─────────────────────────────────────────────────────────────────

export function useSynthesizeSpeech() {
  return trpc.performance.realtime.synthesizeSpeech.useMutation();
}

// ─── Historical Context ──────────────────────────────────────────────────────

export function useHistoricalContext(
  clapperEmail: string,
  epicType: 'seasonal' | 'performance' | 'feedback_session',
  currentFeedbackId: string,
) {
  return trpc.performance.feedback.getHistoricalContext.useQuery(
    { clapperEmail, epicType, currentFeedbackId },
    { enabled: !!clapperEmail && !!currentFeedbackId, refetchOnWindowFocus: false, staleTime: 60_000 },
  );
}

// ─── Mapa de Talento ─────────────────────────────────────────────────────────

export function useTalentMapTeam() {
  return trpc.performance.talentMap.getTeam.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export function useTalentMapEvaluation(employeeEmail: string) {
  return trpc.performance.talentMap.getEvaluation.useQuery(
    { employeeEmail },
    { enabled: !!employeeEmail, refetchOnWindowFocus: false, staleTime: 10_000 },
  );
}

export function useProcessTalentMessage() {
  const utils = trpc.useUtils();
  return trpc.performance.talentMap.processMessage.useMutation({
    onSuccess: (_, vars) => {
      utils.performance.talentMap.getEvaluation.invalidate({ employeeEmail: vars.employeeEmail });
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_procesar_el_mensaje');
    },
  });
}

export function useConfirmTalentCategory() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.talentMap.confirmCategory.useMutation({
    onSuccess: (_, vars) => {
      utils.performance.talentMap.getEvaluation.invalidate({ employeeEmail: vars.employeeEmail });
      utils.performance.talentMap.getTeam.invalidate();
      // Sol 25/09: lo confirmado por el líder se refleja en TODAS las pantallas.
      utils.performance.talentMap.listAllForPeople.invalidate();
      utils.performance.talentMap.getAnalytics.invalidate();
      toast.success(t('hooks.performance.categoria_confirmada'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_confirmar_la_categoria');
    },
  });
}

// ─── D-Talk ──────────────────────────────────────────────────────────────────

export function useDTalkAccess() {
  return trpc.performance.dtalk.getMyAccess.useQuery(undefined, { staleTime: 5 * 60_000 });
}

export function useMyDTalk() {
  return trpc.performance.dtalk.getMyDTalk.useQuery(undefined, { staleTime: 30_000 });
}

export function useProcessDTalkMessage() {
  const utils = trpc.useUtils();
  const client = trpc.useUtils().client;
  // Los turnos van a la Function URL directa (sin el timeout de 30s de
  // CloudFront del FunctionRouter); si la env no está (stage sin redeploy de
  // infra), cae al procedure tRPC clásico.
  return useMutation<DTalkMessageResult, Error, DTalkMessageInput>({
    mutationFn: (input) =>
      DTALK_MESSAGE_URL
        ? sendDTalkMessage(input)
        : (client.performance.dtalk.processMessage.mutate(input) as Promise<DTalkMessageResult>),
    onSuccess: () => {
      utils.performance.dtalk.getMyDTalk.invalidate();
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_procesar_el_mensaje');
    },
  });
}

export function useReopenDTalk() {
  const utils = trpc.useUtils();
  return trpc.performance.dtalk.reopenConversation.useMutation({
    onSuccess: () => {
      utils.performance.dtalk.getMyDTalk.invalidate();
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_reabrir_la_conversacion');
    },
  });
}

export function useTeamDTalk(enabled = true) {
  return trpc.performance.dtalk.getTeamDTalk.useQuery(undefined, { staleTime: 30_000, enabled });
}

export function useDTalkDetail(employeeEmail: string) {
  return trpc.performance.dtalk.getDTalkDetail.useQuery(
    { employeeEmail },
    { enabled: !!employeeEmail, staleTime: 10_000 },
  );
}

export function useDTalkHistory(employeeEmail: string) {
  return trpc.performance.dtalk.getHistory.useQuery(
    { employeeEmail },
    { enabled: !!employeeEmail, staleTime: 10_000 },
  );
}

export function useDTalkCycleConfig() {
  return trpc.performance.dtalk.getCycleConfig.useQuery(undefined, {
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useSetDTalkCycleConfig() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.dtalk.setCycleConfig.useMutation({
    onSuccess: () => {
      utils.performance.dtalk.getCycleConfig.invalidate();
      utils.profile.getVisibility.invalidate(); // la ventana del ciclo decide si D-Talk aparece en el sidebar
      toast.success(t('hooks.performance.configuracion_del_ciclo_guardada'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_guardar_configuracion'),
  });
}

export function useSetDTalkLicenseExclusion() {
  const utils = trpc.useUtils();
  return trpc.performance.dtalk.setLicenseExclusion.useMutation({
    onSuccess: () => {
      utils.performance.dtalk.getTeamDTalk.invalidate();
      utils.performance.dtalk.listAllForPeople.invalidate();
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_la_elegibilidad'),
  });
}

export function useDTalkLogExport() {
  return trpc.performance.dtalk.logExport.useMutation();
}

export function useDTalkAuditLog(enabled: boolean) {
  return trpc.performance.dtalk.getAuditLog.useQuery(undefined, { enabled, staleTime: 30_000 });
}

export function useDTalkSyncAllToSheets() {
  const { t } = useTranslation();
  return trpc.performance.dtalk.syncAllToSheets.useMutation({
    onSuccess: (r: { synced: number; errors?: number; total?: number; configured: boolean }) => {
      if (!r.configured) {
        toast.success(t('hooks.performance.google_sheets_no_esta_configurado_falta'));
        return;
      }
      // El backend ahora devuelve el conteo REAL: si hubo fallas, se avisan
      // (antes decía "sincronizados N" aunque no se hubiera escrito nada).
      if (r.errors && r.errors > 0) {
        toast.warning(`Sincronizados ${r.synced} de ${r.total ?? r.synced + r.errors} — ${r.errors} con error`);
      } else {
        toast.success(`Sincronizados ${r.synced} registros a Google Sheets`);
      }
    },
    onError: (err) => toastError(err, 'common.toast.error_al_sincronizar'),
  });
}

// ─── Cycle Config ─────────────────────────────────────────────────────────────

export function useCycleConfig() {
  return trpc.performance.talentMap.getCycleConfig.useQuery(undefined, {
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useSetCycleConfig() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.talentMap.setCycleConfig.useMutation({
    onSuccess: () => {
      utils.performance.talentMap.getCycleConfig.invalidate();
      toast.success(t('hooks.performance.configuracion_del_ciclo_guardada'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_guardar_configuracion');
    },
  });
}

export function useAllDTalkForPeople(enabled = true) {
  return trpc.performance.dtalk.listAllForPeople.useQuery(undefined, {
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useTalentMapLogExport() {
  return trpc.performance.talentMap.logExport.useMutation();
}

export function useTalentMapAnalytics() {
  return trpc.performance.talentMap.getAnalytics.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function useTalentMapAllForPeople(enabled = true) {
  return trpc.performance.talentMap.listAllForPeople.useQuery(undefined, {
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function useHrbpOverride() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.talentMap.hrbpOverride.useMutation({
    onSuccess: (_, vars) => {
      utils.performance.talentMap.getEvaluation.invalidate({ employeeEmail: vars.employeeEmail });
      utils.performance.talentMap.listAllForPeople.invalidate();
      utils.performance.talentMap.getTeam.invalidate();
      toast.success(t('hooks.performance.categoria_actualizada_por_hrbp'));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_modificar_la_categoria');
    },
  });
}

/** Licencia en el Mapa de Talento (18/09, como D-Talk): saca a la persona del ciclo. */
export function useSetTalentLicense() {
  const utils = trpc.useUtils();
  return trpc.performance.talentMap.setLicense.useMutation({
    onSuccess: () => {
      utils.performance.talentMap.getTeam.invalidate();
      utils.performance.talentMap.listAllForPeople.invalidate();
      utils.performance.talentMap.getAnalytics.invalidate();
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_la_licencia'),
  });
}

export function useTalentMapSyncAll() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.talentMap.syncAllToSheets.useMutation({
    onSuccess: (data) => {
      utils.performance.talentMap.listAllForPeople.invalidate();
      if (data.disabled) {
        // La planilla no está configurada: decirlo, no un "completado" que no escribió nada
        toast.warning(t('hooks.performance.sync_talent_map_disabled'));
        return;
      }
      toast.success(t('hooks.performance.sync_talent_map_ok', { rows: data.synced })
        + (data.errors > 0 ? ' ' + t('hooks.performance.sync_talent_map_errors', { count: data.errors }) : ''));
    },
    onError: (err) => {
      toastError(err, 'common.toast.error_al_sincronizar_con_sheets');
    },
  });
}

// ── Mapa de Talento — Governance de Permisos ──────────────────────────────────

/** Acceso resuelto del usuario al Mapa de Talento (líder / people / hrbp / none). */
export function useTalentMapAccess() {
  return trpc.performance.talentMap.checkAccess.useQuery(undefined, {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTalentMapGovernance() {
  return trpc.performance.talentMapGovernance.getGovernance.useQuery(undefined, { retry: false });
}

export function useTalentMapCommunityPicker() {
  return trpc.performance.talentMapGovernance.listCommunityForPicker.useQuery(undefined, {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTalentMapLeaders() {
  return trpc.performance.talentMapGovernance.listLeaders.useQuery(undefined, {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveTalentMapGovernance() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.performance.talentMapGovernance.setGovernance.useMutation({
    onSuccess: () => {
      utils.performance.talentMapGovernance.getGovernance.invalidate();
      utils.performance.talentMap.checkAccess.invalidate();
      utils.profile.getVisibility.invalidate();
      toast.success(t('hooks.performance.permisos_guardados'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_guardar_permisos'),
  });
}
